import { Product } from 'common/interfaces/agg-page';
import { AggPageFormContext } from 'context/page-form-context';
import {
  AiVariantContext,
  AiVariantMap,
  AiVariantType,
  IMelContext,
  MelContext,
  MelFields,
} from 'modules/mel-ai';
import { aiVariantTypeMap } from 'modules/mel-ai/constants';
import { AiVariant, RequestNewAiVariantBody } from 'modules/mel-ai/types';
import { mapVariants } from 'modules/mel-ai/utils';
import { PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { melApiSlice } from 'store/api/mel-api/mel-api';

const MelProvider = ({ children }: PropsWithChildren) => {
  const { pageId } = useContext(AggPageFormContext);

  const { useFetchAiVariantsMutation, useRequestNewVariantMutation } = melApiSlice;

  const [fetchPageVariants, pageVariantsResult] = useFetchAiVariantsMutation();
  const [fetchProductVariants, productVariantsResult] = useFetchAiVariantsMutation();
  const [requestVariant] = useRequestNewVariantMutation();

  const [currentFieldName, setCurrentFieldName] = useState<MelFields | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<AiVariant | null>(null);
  const [currentProduct, setCurrentProduct] = useState<Product | null>(null);

  const variantsMap = useMemo<AiVariantMap>(() => {
    return mapVariants(pageVariantsResult.data, productVariantsResult.data);
  }, [pageVariantsResult.data, productVariantsResult.data]);

  const handleRequestVariant = (context?: AiVariantContext) => {
    const body: RequestNewAiVariantBody = {
      context,
      entity_id:
        aiVariantTypeMap[currentFieldName] === AiVariantType.Page ? pageId : currentProduct.id,
      pageId,
      requestedContent: currentFieldName ? [currentFieldName] : null,
      type: aiVariantTypeMap[currentFieldName],
    };

    Promise.resolve(requestVariant(body)).then(() => {
      toast('Processing request');
    });
  };

  const handleRegeneratePage = () => {
    Promise.all([
      requestVariant({
        entity_id: pageId,
        requestedContent: [MelFields.BuyingGuide],
        type: AiVariantType.Page,
      }),
      requestVariant({
        entity_id: currentProduct.id,
        requestedContent: [
          MelFields.BottomLine,
          MelFields.Faqs,
          MelFields.Pros,
          ...(currentProduct.value ? [MelFields.Summary] : []),
        ],
        type: AiVariantType.Product,
      }),
    ]).then(() => {
      toast('Processing request');
    });
  };

  useEffect(() => {
    if (pageId) {
      fetchPageVariants({
        ids: pageId ? [pageId] : [],
        type: AiVariantType.Page,
      });
    }
    if (currentProduct) {
      fetchProductVariants({
        ids: currentProduct.id ? [currentProduct.id] : [],
        type: AiVariantType.Product,
      });
    }
  }, [pageId, currentProduct]);

  return (
    <MelContext.Provider
      value={
        {
          aiVariants: {
            aiVariantPageMap: pageVariantsResult.data,
            aiVariantProductMap: productVariantsResult.data,
          },
          award: currentProduct?.value ?? undefined,
          currentFieldName,
          currentProduct,
          handleRegeneratePage,
          handleRequestVariant,
          selectedVariant,
          setCurrentFieldName,
          setCurrentProduct,
          setSelectedVariant,
          variantsMap,
        } as IMelContext
      }
    >
      {children}
    </MelContext.Provider>
  );
};

export { MelProvider };
